// Shared map definitions; values and helpers are maintained here.
import { colorFor } from './palette';
import type { ThemeMode } from './palette';
import { MESHTASTIC_PACKET_COLOR } from './protocols';
import { TRUST_TIER_INFO } from './trust';


// Animation cap, lowered 2026-09-20 (was 40): once CoreScope/meshview were
// actually feeding real sightings, 40 concurrent rings/rays at the phase-1
// sizing read as "dense orange tick glyphs" smeared across every busy
// cluster — an unreadable, over-dense visual. The per-animation visuals were also
// shrunk substantially (see LiveMap.tsx's renderFrame) — this cap and that
// sizing are the same fix, not independent tuning.
//
// Re-checked, not re-tuned, 2026-09-20 during the pulse/ray rework (see
// lib/animation.ts's own header comment): the pulse-only case (92% of
// live packets — broadcasts with no resolvable receiver) now expires in
// PULSE_MS (900ms) instead of the full ray lifetime, so at the live rate
// (~32/min) average concurrency is under 1 — this cap is now a burst
// safety valve, not a routine bottleneck, and 14 stays generous rather
// than needing to move.
export const MAX_CONCURRENT_ANIMATIONS = 14;


// --- meshview route-tree walk (live map, 2026-09-21) -----------------------
// Design premise: every single packet hits many feeders — a real mesh
// should show something like 15-30 feeders receiving each packet, and the
// map should read as very busy; any decrypted packet gets its lines drawn.
// A busy
// meshview packet carries ~40 feeder receptions — animating every line of
// every such packet as its own DOM overlay (the existing per-ray treatment
// above) the instant a busy firehose lands would be the same "map broke at
// scale" failure a sibling project already hit, so this is a deliberately
// DIFFERENT rendering path from the ray/pulse animation above, not a
// variant of it: a single GL source/setData (LiveMap.tsx's per-frame render
// effect), not one DOM element per line or head, so feeder count never
// costs a DOM node.
//
// Originally a flat fan (transmitter straight to every feeder regardless of
// distance); replaced 2026-09-21 by a walk of GET .../route-tree (Task 27)
// to fix a concept error: a packet must never be shown hopping clear across
// the entire map — it needs to flow through the heard feeders as if that
// were the route it actually took. Per-edge feature count is essentially unchanged (~39 tree edges vs ~40
// flat reception lines per busy packet), so the cap below still governs the
// same GL-cost-vs-packet-count relationship the benchmark measured; what
// changed is HOLD TIME (a tree walk's own cascading per-level delays plus a
// deliberately longer hold/fade — see lib/animation.ts — mean each packet's
// geometry now stays on screen far longer than the original ~2.8s flat
// fan), so more packets are naturally alive at once for the same arrival
// rate. Raised in two steps from the original conservative 8: to 32
// (2026-09-20, when the fan first started actually animating and needed to
// visually dominate the inbound first-party ray) and to 64 (2026-09-21, for
// the longer-hold redesign's higher natural overlap) — both steps against
// the SAME standalone MapLibre GL benchmark (headless, --use-gl=swiftshader
// software rendering — no real GPU, a deliberately worse-than-real-hardware
// floor), which never needed re-running for the second step since it
// already covered this exact packet count at this exact per-packet feature
// density:
//   8 packets  (~520 features) -> ~58 fps
//   16 packets (~980 features) -> ~55 fps
//   24 packets (~1500 features) -> ~53 fps
//   32 packets (~2000 features) -> ~47 fps
//   40 packets (~2450 features) -> ~42 fps
//   48 packets (~2980 features) -> ~39 fps
//   64 packets (~4000 features) -> ~35 fps
// The JS side (building the feature list + calling setData) stayed under
// 0.2ms/frame even at 64 packets — GL compositing, not JS, is the real
// cost, and even 64 packets stays comfortably above 30fps on software
// rendering alone; real client GPUs will do noticeably better. Each
// packet's tree still expires on its own computed lifetime (see
// lib/meshview.ts's pruneExpiredRouteWalks) and oldest is evicted first
// here, so this bounds memory/CPU against an unbounded firehose rate rather
// than holding every packet ever seen.
export const MESHVIEW_MAX_CONCURRENT_RECEPTION_PACKETS = 64;


// Feeder reception lines use the existing "external observer, unverified"
// trust-tier colour (TRUST_TIER_INFO.external_unauthenticated, defined
// below) rather than a new hue — these lines ARE that trust tier, literally
// (meshview.py hardcodes trust_tier: "external_unauthenticated" on every
// row), so reusing its colour keeps "this is an unauthenticated external
// observation" meaning the same everywhere it appears (node badges, feed,
// map lines) instead of inventing a second visual vocabulary for the same
// fact. Referenced directly as TRUST_TIER_INFO.external_unauthenticated.color
// at the call site (LiveMap.tsx) — no separate constant needed here.
export const MESHVIEW_FEEDER_LINE_LABEL = "Feeder reception (meshview)";


// --- CoreScope MeshCore carried-path styling (2026-09-21) ------------------
// MeshCore packets carry their OWN literal route (CoreScope's path_json) —
// no inference, no parent-guessing, no confidence tiers, none of the
// tree-building machinery meshview's own route-tree walk needs — this is why
// this feature is genuinely simpler (see lib/corescore.ts's header comment).
// Every drawable segment is equally "known" — the real
// wire route, not a guess — so there is exactly ONE style here, never a
// tiered one. Deliberately set at the SAME visual weight as meshview's own
// "proven"/traceroute tier (MESHVIEW_ROUTE_CONFIDENCE_STYLE.proven /
// MESHVIEW_FLASH_STYLE.proven above) — a carried route is the same kind of
// ground truth a real traceroute is, just reached a different way (carried
// on the packet itself, never backend-inferred from direct-link data) — but
// kept as its OWN named constant rather than importing meshview's `proven`
// value directly, so this never reads as "MeshCore has confidence tiers
// too" to a future maintainer.
export const CORESCORE_PATH_LINE_STYLE: MeshviewRouteConfidenceStyle = { opacityScale: 1, widthScale: 1.3 };

export const CORESCORE_PATH_FLASH_STYLE: MeshviewFlashStyle = { peakRadiusPx: 12, peakOpacity: 1 };


// Colour is PROTOCOL_COLOR.meshcore (defined further below) — the
// established per-protocol colour every other MeshCore marker on this map
// already uses, not a new hue invented for this one feature. Referenced
// directly via colorFor(PROTOCOL_COLOR.meshcore, theme) at the call site
// (LiveMap.tsx), same pattern as MESHVIEW_FEEDER_LINE_LABEL/
// TRUST_TIER_INFO.external_unauthenticated.color above — no separate colour
// constant needed here.
export const CORESCORE_PATH_LINE_LABEL = "Carried path (CoreScope, MeshCore)";


// Independent concurrency bound for held CoreScope path walks — a separate
// counter from meshview's own MESHVIEW_MAX_CONCURRENT_RECEPTION_PACKETS
// (measured against a materially different per-packet feature count: a
// MeshCore carried path tops out at its own hop count, typically single
// digits, never a branching tree of ~39 edges), so exhausting one budget
// can never starve the other. Not wired to meshview's adaptive
// frame-time-measured cap (lib/animation.ts's MESHVIEW_CAP_*) — that
// controller reacts to meshview's own load specifically; coupling a second,
// much lighter feature's eviction to it would bias its adjustments off a
// cost that isn't its own. A plain fixed FIFO cap (AppData.tsx) is enough
// at this feature's real scale.
// Counts walks still waiting for their spread-out start as well as the ones playing. Lowered from 64 to 48 when each
// landing grew from one feature to up to three (burst, orb, ripple) and the line gained a glow layer: a full house
// is roughly the same feature count as before, and the spread keeps only a few walks playing at once anyway.
export const CORESCORE_MAX_CONCURRENT_PATHS = 48;


// --- meshview route-tree confidence styling (Task 27, 2026-09-21) ---------
// The backend's route-tree endpoint tiers every edge's epistemic weight
// (observers/meshview_route_inference.py): only hop_level 0 is ever
// "proven" (a feeder that heard the transmitter directly); every deeper
// edge is inferred from confirmed direct-link data with decreasing
// certainty. Design rule: style by confidence — proven edges are real
// observed receptions and should read as the most solid; assumed edges
// should read as the faintest. A real, typical packet is roughly 1
// proven, 34 inferred, 4 assumed, so most of any drawn tree is inference,
// and the map must look like it rather than implying a confirmed physical
// route. `opacityScale`/`widthScale` multiply the edge's own travel/fade
// opacity and base line width (LiveMap.tsx) — never a separate colour,
// since these edges are still all the same external_unauthenticated trust
// tier and colour is already spoken for by that convention above.
// Mirrors api/types.ts's own MeshviewRouteConfidence — same independent-leaf-
// module convention as ReachabilityPositionStatus below (this file and
// api/types.ts never import from each other).
export type MeshviewRouteConfidence = "proven" | "evidenced" | "inferred" | "assumed";


export const MESHVIEW_ROUTE_CONFIDENCE_ORDER: MeshviewRouteConfidence[] = ["proven", "evidenced", "inferred", "assumed"];


export const MESHVIEW_ROUTE_CONFIDENCE_LABEL: Record<MeshviewRouteConfidence, string> = {
  proven: "Proven",
  evidenced: "Evidenced",
  inferred: "Inferred",
  assumed: "Assumed",
};


export interface MeshviewRouteConfidenceStyle {
  opacityScale: number;
  widthScale: number;
}


export const MESHVIEW_ROUTE_CONFIDENCE_STYLE: Record<MeshviewRouteConfidence, MeshviewRouteConfidenceStyle> = {
  // Opacity floor raised 2026-09-21. The earlier spread (1 / 0.8 / 0.5 /
  // 0.28) was set to make confidence legible, but roughly 79% of edges are
  // inferred or assumed, so in practice it rendered most of the map's line
  // work at 28-50% opacity and measured near-invisible against the basemap
  // (line pixels peaked at 20 per frame against a flash peak of 165). The
  // tiers still rank the same way; the whole range is simply lifted so the
  // weakest is clearly visible rather than merely present. Confidence is
  // conveyed by relative weight, never by being too faint to see.
  proven: { opacityScale: 1, widthScale: 1.3 },
  evidenced: { opacityScale: 0.92, widthScale: 1.15 },
  inferred: { opacityScale: 0.82, widthScale: 1 },
  // The faintest tier, on purpose: low-confidence edges render faintest,
  // still drawn, never hidden (an omitted edge would read as "the packet
  // didn't reach here," which is false; it reads as "we're genuinely
  // unsure of this hop" instead — see lib/meshview.ts's buildRouteWalk,
  // Task 34, for why every edge draws a line regardless of this tier).
  assumed: { opacityScale: 0.72, widthScale: 0.9 },
};


export function isMeshviewRouteConfidence(x: string | null | undefined): x is MeshviewRouteConfidence {
  return x === "proven" || x === "evidenced" || x === "inferred" || x === "assumed";
}


// Unknown/unrecognised falls to the LEAST certain treatment, not the most —
// same "never overstate confidence" rule TrustTierInfo/ReachabilityConfidence
// already follow elsewhere in this file.
export function meshviewRouteConfidenceStyle(raw: string | null | undefined): MeshviewRouteConfidenceStyle {
  return isMeshviewRouteConfidence(raw) ? MESHVIEW_ROUTE_CONFIDENCE_STYLE[raw] : MESHVIEW_ROUTE_CONFIDENCE_STYLE.assumed;
}


// --- meshview receiver-flash styling (2026-09-21 visibility fix) ---------
// A receiver flash (LiveMap.tsx's per-frame render effect, walk.flashes --
// see lib/meshview.ts's buildRouteWalk) is a REAL reception with no drawn
// line -- roughly 79% of all receptions render as one of these and nothing
// else, so if the flash itself is imperceptible, most of the mesh's real
// activity is invisible. Measured problem: the old formula reused
// MESHVIEW_ROUTE_CONFIDENCE_STYLE (radiusPx = 1.5 + 2*widthScale, opacity =
// state.opacity*opacityScale) -- LINE-tuned scales -- producing an
// "inferred" flash ~3.3px @ 50% opacity and an "assumed" one ~2.9px @ 28%
// opacity, both smaller and/or fainter than the static node dot it sits on
// top of (nodes-dots layer, circle-radius interpolates up to 4.5px at full
// opacity). A flash competing against a same-size-or-smaller, same-or-
// higher-opacity static dot never wins the eye.
//
// Fix: a flash gets its OWN peak size/opacity, tiered by confidence (a
// flash can be any of the four tiers -- buildRouteWalk emits one for any
// edge that fails the plausible-hop-km backstop, or has no usable parent,
// regardless of its own confidence), deliberately larger at EVERY tier
// than the node dot's own maximum radius (4.5px) so a flash always reads
// as bigger than topology regardless of zoom, and NOT reusing the
// line-tuned opacity floor -- faintest AMONG THE TIERS is the intent, not
// below the noise floor, so assumed's own peak opacity is lifted well
// clear of the old 0.28 while staying the least prominent of the four.
export interface MeshviewFlashStyle {
  peakRadiusPx: number;
  peakOpacity: number;
}


export const MESHVIEW_FLASH_STYLE: Record<MeshviewRouteConfidence, MeshviewFlashStyle> = {
  proven: { peakRadiusPx: 12, peakOpacity: 1 },
  evidenced: { peakRadiusPx: 10.5, peakOpacity: 0.95 },
  inferred: { peakRadiusPx: 9, peakOpacity: 0.85 },
  assumed: { peakRadiusPx: 7.5, peakOpacity: 0.7 },
};


export function meshviewFlashStyle(raw: string | null | undefined): MeshviewFlashStyle {
  return isMeshviewRouteConfidence(raw) ? MESHVIEW_FLASH_STYLE[raw] : MESHVIEW_FLASH_STYLE.assumed;
}


// A flash's own brief expand-and-fade lifecycle, independent of the
// arrive/hold/fade timing in animation.ts's routeWalkPathState (which still
// governs overall opacity/lifetime) -- motion is what the eye catches, not
// a dot that blinks straight on at full size, so a flash pops outward from
// a fraction of its own peak radius over MESHVIEW_FLASH_GROW_MS, holds at
// peak through the settled/hold phase, then eases back down toward
// MESHVIEW_FLASH_FADE_SHRINK_FLOOR of peak in step with its own opacity
// fade, so growth and shrink read as one continuous pop rather than a size
// cut plus a separate fade.
export const MESHVIEW_FLASH_START_RADIUS_SCALE = 0.35;

export const MESHVIEW_FLASH_GROW_MS = 200;

export const MESHVIEW_FLASH_FADE_SHRINK_FLOOR = 0.5;


// --- Meshview segment plausibility (Task 32, 2026-09-21) ------------------
// Hard invariant: there should never be a packet that hops clear across the
// entire map. That rule already applies to
// "inferred"/"assumed" edges (they never become a drawn MeshviewRoutePath at
// all — see MESHVIEW_ROUTE_CONFIDENCE_STYLE's own header comment and
// lib/meshview.ts's buildRouteWalk), but it was NOT applied to "proven"/
// "evidenced" edges, which includes every real recorded traceroute hop
// (provenance "traceroute" is always confidence "proven" — see
// MESHVIEW_ROUTE_PROVENANCE_COLOR's own comment below). Measuring every
// stored traceroute's hop-to-hop ground distance from node positions
// (n=103 resolvable hop pairs) found a MAX of 11,818.4km — a quarter of the
// way around the Earth, on a "single hop."
//
// Root-caused, not just measured: that 11,818km "hop" was a real node to
// (0, 0) — the child node's own stored position sat at almost exactly null
// island. Auditing all 2,121 nodes with a position: 75 sit at EXACTLY
// (0, 0) (already excluded by hasUsablePosition's strict equality check,
// lib/meshview.ts), and 3 more sit within a fraction of a degree of it
// (0.058/0.032 and 0.013/0.013 among them) — close enough that a real
// device clearly failed to get a GPS fix and fell back to a zero/near-zero
// default, not a real reported location, but FAR enough that `lat === 0 &&
// lon === 0` alone lets them straight through (0.013 is a real number, not
// null/undefined or 0 — the exact same class of gap hasUsablePosition's own
// comment already warns about for the literal (0,0) case). See
// MESHVIEW_NULL_ISLAND_EPSILON_DEG below, and hasUsablePosition, for the
// actual fix — that near-zero position is the real defect; the giant
// segment was only its symptom.
//
// With the null-island epsilon in place, the remaining real distribution is
// unremarkable: median 29.9km, matching the proven/evidenced links already
// on the map. 22 of 103 measured hops still exceed 60km, 10 exceed 100km —
// long mountaintop-to-mountaintop links are a real, if uncommon, feature of
// this mesh's terrain (confirmed: not a second bad-position artifact), so a
// naive 60km cutoff would silently discard genuine data. This ceiling is
// therefore a BACKSTOP for whatever the null-island fix doesn't catch (a
// differently-corrupted position — a sign error, a stale fix an ocean away
// — is still possible and the null-island epsilon is deliberately narrow,
// see that constant's own comment for why it must stay that way), not the
// primary defense, and is set generously enough to draw every real hop
// measured here.
//
// The backend already draws exactly this real-vs-impossible distinction
// twice, at two different ceilings, for two different questions (see
// backend/amalgamesh/config_schema.py):
//   route_inference.max_single_hop_miles   (60mi / 96.56km) answers "is this
//     an unconfirmed candidate ancestor plausible enough to GUESS as one" --
//     deliberately conservative, because being wrong just produces a worse
//     guess.
//   audit.reachability_max_plausible_miles (500mi / 804.67km) answers "is
//     this reported distance an exceptional-but-real reach, or is it
//     impossible" -- explicitly documented there as a "record-reach
//     ceiling," distinct from and looser than the 60mi guess threshold,
//     because "an exceptional reach is not what 'plausible single hop'
//     should mean here."
// A traceroute segment is a KNOWN recorded hop, not a guess -- the question
// this frontend needs answered for it is the second one (is this endpoint
// pair even possible), not the first (is this a good guess). So this ceiling
// mirrors audit.reachability_max_plausible_miles's value, not
// route_inference.max_single_hop_miles's, and applies to every edge that
// would otherwise become a drawn line (proven or evidenced, traceroute or
// not) -- the frontend and backend don't share code, so this is a plain
// frontend constant, not an import, but it is deliberately the SAME number
// as the backend's own "exceptional but real" ceiling rather than an
// unrelated one invented here. A skipped segment's own far node still
// flashes (lib/meshview.ts's buildRouteWalk) -- losing the LINE for one bad
// hop must never lose the fact that the packet was genuinely heard there.
// Tightened from 804.67km (500mi) on 2026-09-21 after measuring every stored
// traceroute hop against real node positions: 97 of 103 sit under 150km
// (median 29.9km), and the 6 that exceed it cluster on a handful of nodes --
// "Northgate Pass Router" alone accounts for four of them (234/228/177/164km),
// with Ashcroft->Redpine Peak Router at 456km and Ridgeline Mountain Router->
// drivec 3 Base at 412km. A node recurring across several implausible hops is
// the signature of a WRONG STORED POSITION, not an exceptional antenna, so
// these are excluded from being DRAWN rather than trusted, reinforcing the same
// hard invariant: there should never be a packet that hops clear across the
// entire map. The far node still flashes -- withholding the line never withholds the
// fact that the packet was genuinely heard there. Those nodes want a real
// position audit; this constant is the render-side backstop, not the fix.
export const MESHVIEW_MAX_PLAUSIBLE_HOP_KM = 150;


// Matches backend's own audit/position_plausibility.py
// DEFAULT_NULL_ISLAND_EPSILON_DEG (0.1 degrees) -- reused here, not
// reinvented, same reasoning as MESHVIEW_MAX_PLAUSIBLE_HOP_KM mirroring a
// backend ceiling rather than picking an unrelated frontend-only number.
// Deliberately a small, LITERAL epsilon around the origin only, never a
// broader magnitude or regional check: nodes far outside this deployment's
// core coverage area (e.g. real, observed MeshCore nodes ~800km away) are
// legitimate positions and must never be excluded by this rule — a
// geographic allowlist/blocklist would wrongly discard real data. This is
// the ONE shared rule for "is this position actually a null-island sentinel
// masquerading as real data" — hasUsablePosition (lib/meshview.ts) is the
// single place it's applied, and every caller of hasUsablePosition (the
// route-tree walk's root/edge checks, and AppData.tsx's own pulse-origin
// checks) gets it for free, rather than each maintaining its own copy of
// "how close to zero is too close."
export const MESHVIEW_NULL_ISLAND_EPSILON_DEG = 0.1;


// --- meshview route-tree PROVENANCE styling (Task 28, 2026-09-21) ---------
// Separate axis from MESHVIEW_ROUTE_CONFIDENCE_STYLE above: confidence says
// how CERTAIN an edge's parentage is (proven/evidenced/inferred/assumed);
// provenance says whether the edge came from a real TRACEROUTE_APP packet's
// own recorded, ordered path (backend commit b105f69,
// observers/meshview_route_inference.py's build_traceroute_edges) or from
// the existing confirmed-direct-link inference. A traceroute edge is always
// confidence="proven" already (same tier a directly-heard hop-0 reception
// gets), so confidence styling alone can't tell the two apart — provenance
// needs its own multiplier, applied ON TOP of confidence, never instead of
// it. Traceroutes get priority because the whole route is visible —
// this is the one case where the map can honestly say "this is the route
// it took," so it must read as more prominent than an inferred edge at the
// very same confidence tier. Colour reuses MESHTASTIC_PACKET_COLOR's own
// "traceroute" swatch — the SAME magenta the legend's "Traceroute" packet-
// type entry already shows — rather than inventing a second colour for the
// same real-world fact; an inferred edge gets its own line colour, below —
// see MESHVIEW_ROUTE_LINE_INFERRED_COLOR for why it is no longer the raw
// external_unauthenticated swatch.
export type MeshviewRouteProvenance = "traceroute" | "inferred";


// The receiver flash's own colour (LiveMap.tsx's per-frame render effect) —
// literally TRUST_TIER_INFO.external_unauthenticated.color, unchanged since
// the 2026-09-21 flash-sizing fix (092c862). Split into its own named
// constant (2026-09-21, colour-not-size fix) so it can be referenced
// directly by the flash's own render call instead of through
// meshviewRouteLineColor("inferred", ...), which now returns a DIFFERENT
// value — see that constant's comment below for why.
export const MESHVIEW_FLASH_COLOR = TRUST_TIER_INFO.external_unauthenticated.color;


// An "inferred"-provenance route LINE's own colour, deliberately DIFFERENT
// from MESHVIEW_FLASH_COLOR (2026-09-21, colour-not-size fix). Root cause of
// "lines are invisible" was never size or z-order (092c862 and 4e89db9 were
// both correct and are unchanged) — it was that a line and a flash painted
// in the identical #ec835a, and a justified hop is median <10km (~6 screen
// px at default zoom) while a flash is a 24px-wide blob, so a short line is
// drawn entirely inside a same-coloured blob of its own endpoint. The fix
// is colour, not size — flashes keep 092c862's peak sizing exactly.
//
// Deliberately kept in the SAME hue family as external_unauthenticated
// (~17 degrees, orange-red — computed from #ec835a's own HSL) rather than a
// different hue entirely: line and flash are honestly the same trust tier
// (meshview.py hardcodes trust_tier: "external_unauthenticated" on every
// row) and this must read as two ROLES of one provenance, never as two
// different trust levels. What changes is lightness/saturation only — a
// darker, more saturated rust/terracotta at the same hue angle — which
// keeps enough RGB distance from the flash's own #ec835a (channel deltas of
// 40-75) to stay legible as a distinct stroke directly on top of a flash of
// the parent colour, in both themes (kept theme-invariant, like every other
// STATUS/trust-tier swatch this derives from).
export const MESHVIEW_ROUTE_LINE_INFERRED_COLOR = { light: "#c2440f", dark: "#c2440f" };


export const MESHVIEW_ROUTE_PROVENANCE_COLOR: Record<MeshviewRouteProvenance, { light: string; dark: string }> = {
  traceroute: MESHTASTIC_PACKET_COLOR.traceroute,
  inferred: MESHVIEW_ROUTE_LINE_INFERRED_COLOR,
};


export interface MeshviewRouteProvenanceStyle {
  opacityScale: number;
  widthScale: number;
}


export const MESHVIEW_ROUTE_PROVENANCE_STYLE: Record<MeshviewRouteProvenance, MeshviewRouteProvenanceStyle> = {
  // Opacity is already at its ceiling via confidence (proven=1) — the
  // distinguishing weight here is width, kept clear of
  // MESHVIEW_TRAIL_HEAD_OPACITY's own headroom-for-overlap reasoning.
  traceroute: { opacityScale: 1, widthScale: 1.7 },
  inferred: { opacityScale: 1, widthScale: 1 },
};


export function isMeshviewRouteProvenance(x: string | null | undefined): x is MeshviewRouteProvenance {
  return x === "traceroute" || x === "inferred";
}


// Unknown/unrecognised provenance falls to "inferred" (the less-prominent
// treatment) — same "never overstate confidence" rule
// meshviewRouteConfidenceStyle above already follows for its own axis.
export function meshviewRouteProvenanceStyle(raw: string | null | undefined): MeshviewRouteProvenanceStyle {
  return isMeshviewRouteProvenance(raw) ? MESHVIEW_ROUTE_PROVENANCE_STYLE[raw] : MESHVIEW_ROUTE_PROVENANCE_STYLE.inferred;
}


export function meshviewRouteLineColor(raw: string | null | undefined, mode: ThemeMode): string {
  const key: MeshviewRouteProvenance = isMeshviewRouteProvenance(raw) ? raw : "inferred";
  return colorFor(MESHVIEW_ROUTE_PROVENANCE_COLOR[key], mode);
}


// --- Initial camera fit (live map, 2026-09-21) ------------------------------
// Requirement: the map must not auto-fit only part of the mesh — it used to
// always open on a fixed regional default
// view (LiveMap.tsx's `new MLMap({ center, zoom })`) regardless of where the
// mesh's own nodes actually are. This is deliberately a PADDING + MAX-ZOOM
// pair, not a single "default zoom" constant, because a fit's own zoom level
// is derived from the mesh's actual bounding box every time (tight cluster ->
// zooms in; sprawling mesh -> zooms out) — MESH_FIT_MAX_ZOOM only clamps the
// tight-cluster end so a mesh of a handful of nodes standing right next to
// each other doesn't zoom in absurdly far. This mesh's own nodes are NOT all
// regional: real MeshCore nodes have been observed ~800km
// from this deployment's core coverage area, so the fit must cover the true bounding box of every
// node with a usable position (lib/meshview.ts's hasUsablePosition — the same
// null/null-island rule used everywhere else this app decides "is this
// position real"), never a regional subset.
export const MESH_FIT_PADDING_PX = 56;

export const MESH_FIT_MAX_ZOOM = 12;


// Settings-coverage pass (part 4, 2026-09-27): the map's own fallback view
// when there is neither an operator-configured map.default_center_*/
// default_zoom (GET /api/config/display) NOR any node with a usable
// position yet — replacing the previous hardcoded regional default
// ([-56.2, 43.6], zoom 6.2) this deployment happened to use, which is not
// a general fact about any amalgamesh install. A wide zoomed-out world
// view, never a silently-assumed region.
export const WORLD_VIEW_CENTER: [number, number] = [0, 20];

export const WORLD_VIEW_ZOOM = 1.5;


// A single bad stored position can otherwise define the ENTIRE fit — found
// live 2026-09-21: "XY Repeater 2" (meshcore) sits at 37.68N/53.06E (overseas),
// the only one of 1,684 usable-position nodes outside roughly -62.8..-51.1
// longitude, and it alone pulled the fit out to a near-global zoom 1.5 view.
// The real spread measured ~12 degrees of latitude and ~12 of
// longitude edge to edge across the deployment's nodes — percentile trimming
// was explicitly ruled out
// (62 of 1,684 nodes fall outside p1-p99 and most, including an entire
// legitimate distant cluster, are real mesh, not outliers) in favour of this: exclude
// only a node whose position sits implausibly far from where the mesh
// actually is. This is deliberately NOT a region filter (no hardcoded
// lat/lon box) — it is computed fresh from the median of whatever positions
// exist, so it keeps working unchanged if the mesh moves or grows. A
// generous margin over the real ~12-degree spread, never the fit's own
// bounding radius. This gates ONLY which nodes count toward the CAMERA fit
// (LiveMap.tsx's meshBoundsFromNodes) — excluded nodes still render on the
// map like any other node; this never touches hasUsablePosition, the
// animation, or anything upstream of the frontend.
export const MESH_FIT_OUTLIER_MAX_DEGREES_FROM_MEDIAN = 28;


// Label for the manual "re-fit to the mesh" map control (added alongside the
// initial auto-fit) — the auto-fit only ever runs once, on first load, and
// deliberately never re-centres under a user who has already panned/zoomed
// (the failure mode to avoid: a camera that fights the user), so this is
// the one place left to ask for that same fit again on demand.
export const MESH_FIT_CONTROL_LABEL = "Fit to mesh";


// --- Route provenance (live-map spec §4.3) ---------------------------------
// "A MeshCore route is known. A Meshtastic path is inferred. They must never
// look identical on the map." Audited 2026-09-20 against
// amalgamesh-live-map-spec.md: the backend already carries real MeshCore
// route data (`route_hops` — confirmed live, an ordered array of relay hash
// + SNR entries) but has no Meshtastic path-inference pipeline at all (no
// correlate/order/anchor/bound/score computation, no endpoint for it) —
// that half of §4.2 is a genuine backend gap, not something to fake here.
// This helper only ever states what's actually known: a non-empty
// route_hops on a MeshCore packet is a real carried route (§4.1); anything
// else — including every Meshtastic packet, since no inference exists to
// report — gets no route claim at all, exactly matching §4.4's "no path at
// all" fallback rather than implying an inference we haven't built.
export function routeProvenanceLabel(protocol: string, routeHops: unknown[] | null | undefined): string | null {
  if (protocol === "meshcore" && Array.isArray(routeHops) && routeHops.length > 0) {
    return `Known route — carried in packet, ${routeHops.length} hop${routeHops.length === 1 ? "" : "s"}.`;
  }
  return null;
}
